<template>
  <div class="i-professions-list">
    <iBaseHeader
      highlight-text=""
      text="Top Recommendations"
      font-size-text="16px"
      align="start"
    />
    <div
      v-if="jobs && jobs.length > 0"
      class="list"
    >
      <div
        v-for="job in props.jobs"
        :key="job.id"
        class="list-item"
        @click="$emit('jobPageRedirect', job.id)"
      >
        <iBaseTruncatedText
          :text="job.title"
          :max-length="45"
        />
        <div
          class="badge"
          :style="{
            backgroundColor: job.isFavorite ? sorbus100 : job.color,
            borderColor: job.isFavorite ? sorbus300 : job.color
          }"
        >
          <img
            v-if="job.isFavorite"
            :src="iconStarOrange"
          />
          <template v-else>{{ job.badge }}</template>
        </div>
      </div>
    </div>
    <div class="bottom">
      <iOnboardingTooltip :value="2" />
      <div
        v-if="userStore.isTierFree"
        class="unlock"
      >
        <iUnlockCareers
          :is-compact="true"
          @upgrade-clicked="emit('upgradeClicked')"
        />
      </div>
      <iBaseTextCta
        v-else
        text="See Full List"
        @cta-clicked="goToCareersOverview()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from "vue"
import { useRouter } from "vue-router"
import { useUserStore } from "@/stores/user"
import { sorbus100, sorbus300 } from "@/styles/colors"

import { GlobalVariables } from "@/helpers/globalVariables"

import iBaseHeader from "@/components/base/BaseHeader.vue"
import iBaseTextCta from "@/components/base/BaseTextCta.vue"
import iBaseTruncatedText from "@/components/base/BaseTruncatedText.vue"
import iUnlockCareers from "@/components/report/UnlockCareers.vue"
import iOnboardingTooltip from "@/components/report/OnboardingTooltip.vue"

// ASSETS
import iconStarOrange from "@/assets/icons/icon-star-orange-filled.svg"

//TYPES
import type { ExtendedRecommendedJob } from "@/types"

//EMITS
const emit = defineEmits(["jobPageRedirect", "upgradeClicked"])

// STORES AND COMPOSABLES
const userStore = useUserStore()

// ROUTES
const router = useRouter()

// PROPS
const props = defineProps({
  jobs: {
    type: Array as PropType<ExtendedRecommendedJob[]>,
    required: true
  }
})

// FUNCTIONS
const goToCareersOverview = () => {
  router.push({ name: GlobalVariables.urls.report.children.careersOverview.routeName })
}
</script>

<style lang="scss" scoped>
.i-professions-list {
  @include flex(column, start, stretch, $gap: 15px);

  padding: 20px 20px 0;
  width: 100%;
  height: 100%;

  @media screen and (min-width: $breakpoint-xl) {
    padding: 20px;
    border-bottom: 1px solid $zumthor100;
  }

  .list {
    @include flex(column, start, stretch);

    .list-item {
      @include flex(row, $justify: space-between, $align: center, $gap: 5px);

      padding: 13px 5px;
      width: 100%;
      cursor: pointer;

      @include text-md;

      color: $pickle-bluewood800;

      &:not(:last-child),
      &:first-child {
        border-bottom: 1px solid $zumthor100;
      }

      &:hover {
        background-color: $azure-radiance50;
      }

      .badge {
        @include border-radius-lg;

        white-space: nowrap;
        color: $white;
        border-width: 1px;
        border-style: solid;
        padding: 2px 6px;

        @include text-xs;

        @media screen and (min-width: $breakpoint-sm) {
          padding: 2px 10px;

          @include text-sm;
        }

        @media screen and (min-width: $breakpoint-xl) {
          padding: 2px 6px;

          @include text-xs;
        }

        img {
          vertical-align: text-bottom;
        }
      }
    }
  }

  .bottom {
    border-bottom: 1px solid $zumthor100;
    padding-bottom: 20px;
    position: relative;

    @media screen and (min-width: $breakpoint-xl) {
      padding-bottom: 0;
      border: none;
    }

    .unlock {
      width: 100%;
      margin-top: 20px;
    }
  }
}
</style>
